import { useEffect, useState, Fragment } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge, EmptyState } from "@/components/Shared";
import HealthBadge from "@/components/HealthBadge";
import EmbeddedDocuments from "@/components/EmbeddedDocuments";
import VehicleFormWizard, { VEHICLE_EMPTY } from "@/components/VehicleFormWizard";
import { usePermissions } from "@/contexts/PermissionsContext";
import { ArrowLeft, Plus, X, Trash2, Pencil, Camera, Upload, AlertOctagon, AlertTriangle, User, MapPin, Gauge, Truck, ChevronDown, ChevronRight, CreditCard, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { compressImage, fmtBytes } from "@/lib/imageCompressor";
import { fmtCur, fmtDate } from "@/lib/format";

const TABS = [
  "Übersicht", "Stammdaten", "Dokumente", "Versicherung", "Leasing/Finanzierung",
  "Service", "Pickerl / §57a", "Schäden", "Tankkarten", "Übergaben", "Kosten", "Audit",
];

export default function FuhrparkProfile() {
  const { id } = useParams();
  const { can } = usePermissions();
  const perms = {
    create: can("fuhrpark", "create"),
    edit: can("fuhrpark", "edit"),
    delete: can("fuhrpark", "delete"),
  };
  const [v, setV] = useState(null);
  const [tab, setTab] = useState("Übersicht");
  const [docs, setDocs] = useState([]);
  const [audit, setAudit] = useState([]);
  const [damages, setDamages] = useState([]);
  const [handovers, setHandovers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [fuelCards, setFuelCards] = useState([]);
  const [leasings, setLeasings] = useState([]);

  async function reload() {
    const { data } = await api.get(`/vehicles/${id}`); setV(data);
    api.get("/documents", { params: { owner_type: "vehicle", owner_id: id } }).then(({data}) => setDocs(data));
    api.get("/audit").then(({data}) => setAudit(data.filter(a => a.record_id === id)));
    api.get("/damages", { params: { vehicle_id: id } }).then(({data}) => setDamages(data));
    api.get("/handovers", { params: { vehicle_id: id } }).then(({data}) => setHandovers(data));
    api.get("/fuel-cards", { params: { vehicle_id: id } }).then(({data}) => setFuelCards(data));
    api.get("/leasings", { params: { vehicle_id: id } }).then(({data}) => setLeasings(data));
  }

  useEffect(() => {
    reload();
    api.get("/employees").then(({data}) => setEmployees(data));
    api.get("/checklists").then(({data}) => setChecklists(data));
  }, [id]);

  const empName = (eid) => { const e = employees.find(x => x.id === eid); return e ? `${e.first_name} ${e.last_name}` : "—"; };

  if (!v) return <div className="p-8 text-zinc-500">Lade Fahrzeug...</div>;

  return (
    <div className="p-6 md:p-8" data-testid="vehicle-profile">
      <Link to="/fuhrpark" className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-200 mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Zurück
      </Link>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6 mb-6">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-start gap-5">
            <div className="w-20 h-20 rounded-md bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 shrink-0">
              <Truck className="w-9 h-9"/>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Fahrzeug · {v.vehicle_type}</div>
              <h1 className="font-display text-2xl md:text-3xl font-bold text-zinc-50 mt-1 font-mono">{v.license_plate}</h1>
              <div className="text-sm text-zinc-300 mt-1">{v.brand} {v.model}</div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <StatusBadge status={v.status} />
                {v.health_level && <HealthBadge level={v.health_level} reasons={v.health_reasons || []} size="xs" testid="veh-profile-health"/>}
                {v.department && <span className="text-xs text-zinc-400">{v.department}</span>}
                {v.workplace && <><span className="text-zinc-600">·</span><span className="text-xs text-zinc-400 inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{v.workplace}</span></>}
                <span className="text-zinc-600">·</span>
                <span className="text-xs text-zinc-400 inline-flex items-center gap-1 font-mono"><Gauge className="w-3 h-3"/>{(v.current_km || 0).toLocaleString("de-AT")} km</span>
                <span className="text-zinc-600">·</span>
                <span className={`text-xs font-mono ${v.adblue_percent < 20 ? "text-red-400" : v.adblue_percent < 40 ? "text-amber-400" : "text-zinc-400"}`}>AdBlue {v.adblue_percent}%</span>
              </div>
              {v.current_driver && (
                <div className="mt-2 inline-flex items-center gap-2 px-2 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded text-xs">
                  <User className="w-3 h-3 text-emerald-400"/>
                  <span className="text-emerald-400 font-semibold">Fahrer:</span>
                  <Link to={`/mitarbeiter/${v.current_driver.id}`} className="text-zinc-100 hover:text-blue-400">{v.current_driver.name}</Link>
                  <span className="text-zinc-500">seit {v.current_driver.since}</span>
                </div>
              )}
            </div>
          </div>

          {/* ALERT BADGES */}
          <VehicleAlertBadges v={v} docs={docs} damages={damages}/>
        </div>
      </div>

      <div className="border-b border-zinc-800 mb-6">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`veh-tab-${t}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                tab === t ? "text-zinc-50 border-blue-600" : "text-zinc-400 hover:text-zinc-200 border-transparent"
              }`}>{t}</button>
          ))}
        </div>
      </div>

      {tab === "Übersicht" && <Overview v={v} />}
      {tab === "Stammdaten" && <VehicleStammdatenTab v={v} canEdit={perms.edit} onSaved={(updated) => setV(updated)}/>}
      {tab === "Dokumente" && <EmbeddedDocuments ownerType="vehicle" ownerId={id} ownerLabel={v.license_plate} onChange={reload}/>}
      {tab === "Versicherung" && <InfoPanel items={[
        ["Versicherung gültig bis", v.insurance_date],
        ["Status", v.insurance_date && v.insurance_date >= new Date().toISOString().slice(0,10) ? "Aktiv" : "Fehlt / abgelaufen"],
      ]}/>}
      {tab === "Leasing/Finanzierung" && <LeasingsTab vehicleId={id} items={leasings} onChange={reload} perms={perms} />}
      {tab === "Service" && <InfoPanel items={[
        ["Nächster Service (Datum)", v.next_service_date],
        ["Aktueller km-Stand", (v.current_km||0).toLocaleString("de-AT")],
      ]}/>}
      {tab === "Pickerl / §57a" && <InfoPanel items={[
        ["§57a / Pickerl fällig", v.pickerl_date],
        ["Status", v.pickerl_date && v.pickerl_date >= new Date().toISOString().slice(0,10) ? "Gültig" : "Fehlt / abgelaufen"],
      ]}/>}
      {tab === "Schäden" && <DamagesTab vehicleId={id} damages={damages} employees={employees} onChange={reload} perms={perms} />}
      {tab === "Tankkarten" && <FuelCardsTab vehicleId={id} items={fuelCards} onChange={reload} perms={perms} />}
      {tab === "Übergaben" && <HandoversTab vehicleId={id} handovers={handovers} employees={employees} checklists={checklists} onChange={reload} perms={perms} />}
      {tab === "Kosten" && <CostSummaryTab vid={id}/>}
      {tab === "Audit" && <AuditTable audit={audit} />}
    </div>
  );
}

function VehicleAlertBadges({ v, docs, damages }) {
  const today = new Date().toISOString().slice(0, 10);
  const soon30 = new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);
  const badges = [];
  const insMissing = !v.insurance_date || v.insurance_date < today;
  const pickerlMissing = !v.pickerl_date || v.pickerl_date < today;
  const insSoon = !insMissing && v.insurance_date <= soon30;
  const pickerlSoon = !pickerlMissing && v.pickerl_date <= soon30;
  const serviceSoon = false; // next_service_km removed in H1 Spring-Cleaning; service-soon now date-based via expiring-vehicles dashboard
  const openDamages = damages.filter((d) => d.status !== "behoben").length;
  const expiredDocs = docs.filter((d) => d.doc_status === "abgelaufen").length;
  const expiringDocs = docs.filter((d) => d.doc_status === "bald").length;

  if (insMissing) badges.push({ tone: "critical", label: "Versicherung fehlt/abgelaufen", icon: AlertOctagon });
  if (pickerlMissing) badges.push({ tone: "critical", label: "§57a fehlt/abgelaufen", icon: AlertOctagon });
  if (expiredDocs > 0) badges.push({ tone: "critical", label: `${expiredDocs} abgel. Dokumente`, icon: AlertOctagon });
  if (openDamages > 0) badges.push({ tone: "warning", label: `${openDamages} offene Schäden`, icon: AlertTriangle });
  if (insSoon) badges.push({ tone: "warning", label: "Versicherung ≤ 30 T", icon: AlertTriangle });
  if (pickerlSoon) badges.push({ tone: "warning", label: "§57a ≤ 30 T", icon: AlertTriangle });
  if (serviceSoon) badges.push({ tone: "warning", label: "Service fällig", icon: AlertTriangle });
  if (expiringDocs > 0) badges.push({ tone: "warning", label: `${expiringDocs} Dokumente ≤ 30 T`, icon: AlertTriangle });

  const colors = {
    critical: "bg-red-500/10 text-red-400 border-red-500/30",
    warning: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  };
  if (badges.length === 0) {
    return <span className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded">Alles ok</span>;
  }
  return (
    <div className="flex flex-wrap gap-2" data-testid="vehicle-alert-badges">
      {badges.map((b, i) => (
        <div key={i} className={`px-3 py-1.5 text-xs border rounded-md inline-flex items-center gap-2 ${colors[b.tone]}`}>
          <b.icon className="w-3.5 h-3.5"/>
          <span>{b.label}</span>
        </div>
      ))}
    </div>
  );
}

function VehicleStammdatenTab({ v, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...VEHICLE_EMPTY, ...v });

  function openEdit() {
    setForm({ ...VEHICLE_EMPTY, ...v });
    setEditing(true);
  }

  async function handleSave() {
    try {
      const { data } = await api.put(`/vehicles/${v.id}`, form);
      toast.success("Stammdaten gespeichert");
      onSaved?.(data);
      setEditing(false);
    } catch {
      toast.error("Fehler beim Speichern");
    }
  }

  const tf = (b) => (b ? "Ja" : "Nein");
  const sections = [
    {
      title: "Identifikation",
      rows: [
        ["Kennzeichen", v.license_plate], ["Identifikationsnummer", v.vin],
        ["Marke", v.brand], ["Modell", v.model],
        ["Fahrzeugtyp", v.vehicle_type],
      ],
    },
    {
      title: "Anhänger",
      rows: [
        ["Anhänger", v.trailer],
      ],
    },
    {
      title: "Einsatz",
      rows: [
        ["Abteilung", v.department], ["Einsatzort", v.workplace],
        ["Stellplatz", v.parking_place], ["Status", v.status],
      ],
    },
    {
      title: "Termine & Fristen",
      rows: [
        ["Eingetragenes Datum", v.registration_date],
        ["EZ / §57a Pickerl", v.pickerl_date],
        ["Garantielaufzeit", v.warranty_expiry],
        ["Versicherung bis", v.insurance_date],
        ["Abmeldedatum", v.deregistration_date],
        ["Nächster Service", v.next_service_date],
      ],
    },
    {
      title: "Technisch",
      rows: [
        ["Leistung (kW)", v.power_kw],
        ["Kraftstoff", v.fuel_type],
        ["AdBlue %", `${v.adblue_percent || 0}%`],
        ["Letzter Km-Status", (v.current_km || 0).toLocaleString("de-AT")],
        ["Verbrauch /100 km", v.consumption_per_100km],
        ["Verbrauchs-Toleranz +-", v.consumption_tolerance],
      ],
    },
    {
      title: "Maße & Reifen",
      rows: [
        ["Reifendimension", v.tire_dimension],
        ["Nutzlast (kg)", v.payload_kg],
        ["Volumen m³", v.volume_m3],
        ["Innere Breite (cm)", v.inner_width_cm],
        ["Innere Höhe (cm)", v.inner_height_cm],
        ["Innere Länge (cm)", v.inner_length_cm],
      ],
    },
    {
      title: "Marketing",
      rows: [
        ["Besondere Merkmale", v.special_features],
        ["Branding", v.branding],
        ["Vignette / Go-Box", v.vignette],
      ],
    },
  ];
  return (
    <div className="space-y-5" data-testid="vehicle-stammdaten-tab">
      {canEdit && (
        <div className="flex justify-end">
          <button
            data-testid="vehicle-stammdaten-edit-btn"
            onClick={openEdit}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
          >
            <Pencil className="w-4 h-4" /> Stammdaten bearbeiten
          </button>
        </div>
      )}
      {sections.map((s) => (
        <div key={s.title} className="bg-zinc-900 border border-zinc-800 rounded-md">
          <div className="px-5 py-3 border-b border-zinc-800 text-sm font-semibold text-zinc-100">{s.title}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 divide-zinc-800">
            {s.rows.map(([k, val]) => (
              <div key={k} className="px-5 py-3 border-b border-zinc-800 last:border-0 md:[&:nth-last-child(-n+2)]:border-b-0 md:odd:border-r">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
                <div className="text-sm text-zinc-100 mt-0.5 font-mono">
                  {(val ?? "") !== "" && val !== 0 ? val : (val === 0 ? "0" : "—")}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {editing && (
        <VehicleFormWizard
          form={form}
          setForm={setForm}
          editing={true}
          onClose={() => setEditing(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function Overview({ v }) {
  return (
    <div className="space-y-4">
      {v.current_driver && (
        <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-md p-4 flex items-center justify-between" data-testid="current-driver-panel">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-1">Aktueller Fahrer</div>
            <div className="text-lg font-semibold text-zinc-100">{v.current_driver.name}</div>
            <div className="text-xs text-zinc-400 font-mono">
              {v.current_driver.phone || "—"} · übergeben seit {v.current_driver.since}
            </div>
          </div>
          <a href={`/mitarbeiter/${v.current_driver.id}`} className="text-xs text-emerald-400 hover:text-emerald-300 uppercase tracking-wider font-semibold">Profil →</a>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          ["VIN / FIN", v.vin], ["Abteilung", v.department], ["Einsatzort", v.workplace],
          ["Stellplatz", v.parking_place], ["Aktuelle KM", (v.current_km||0).toLocaleString("de-AT")],
          ["AdBlue", `${v.adblue_percent}%`], ["Pickerl § 57a", v.pickerl_date],
          ["Versicherung bis", v.insurance_date], ["Nächster Service", v.next_service_date],
          ["Notizen", v.notes],
        ].map(([k, val]) => (
          <div key={k} className="bg-zinc-900 border border-zinc-800 rounded p-4">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
            <div className="text-sm text-zinc-100 mt-1 font-mono">{val || "—"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CostSummaryTab({ vid }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/vehicles/${vid}/cost-summary`, { params: { year } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, [vid, year]);

  const fmt = fmtCur;

  return (
    <div data-testid="cost-summary-tab">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5 mb-4 flex items-center gap-3">
        <label className="text-[10px] uppercase tracking-wider text-zinc-500">Jahr</label>
        <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100" data-testid="cost-year">
          {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {!data ? <div className="text-zinc-500 text-sm">Lade...</div> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
            <CostCard label="Leasing" value={fmt(data.leasing.total)} hint={`${data.leasing.entries.length} Vertrag/Verträge`} tone="warning"/>
            <CostCard label="Schäden" value={fmt(data.damages.total)} hint={`${data.damages.entries.length} Einträge`} tone={data.damages.total > 0 ? "critical" : "default"}/>
            <CostCard label="Treibstoff (geschätzt)" value={fmt(data.fuel_estimated)} hint="Tankkarten-Limit × 12" tone="default"/>
            <CostCard label="Gesamt" value={fmt(data.grand_total)} hint={`Jahr ${year}`} tone="critical" highlight/>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5">
              <h4 className="text-sm font-semibold text-zinc-100 mb-3">Leasing-Verträge</h4>
              {data.leasing.entries.length === 0 ? <div className="text-xs text-zinc-500">Keine Verträge</div> : (
                <div className="space-y-2">
                  {data.leasing.entries.map((l, i) => (
                    <div key={l.id || `leasing-${i}`} className="flex items-center justify-between py-1.5 border-b border-zinc-800 last:border-0">
                      <div>
                        <div className="text-sm text-zinc-100">{l.provider}</div>
                        <div className="text-xs text-zinc-500 font-mono">{l.contract_number || "—"} · {fmt(l.monthly_rate)}/Monat × {l.months} Monate</div>
                      </div>
                      <div className="text-sm font-mono tabular-nums text-zinc-100">{fmt(l.total)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5">
              <h4 className="text-sm font-semibold text-zinc-100 mb-3">Schäden</h4>
              {data.damages.entries.length === 0 ? <div className="text-xs text-zinc-500">Keine Schäden in diesem Jahr</div> : (
                <div className="space-y-2">
                  {data.damages.entries.map((d, i) => (
                    <div key={d.id || `damage-${i}`} className="flex items-center justify-between py-1.5 border-b border-zinc-800 last:border-0">
                      <div>
                        <div className="text-sm text-zinc-100 truncate max-w-[280px]">{d.description}</div>
                        <div className="text-xs text-zinc-500 font-mono">{fmtDate(d.date)} · {d.status}</div>
                      </div>
                      <div className="text-sm font-mono tabular-nums text-red-400">{fmt(d.cost)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5 lg:col-span-2">
              <h4 className="text-sm font-semibold text-zinc-100 mb-3">Versicherung</h4>
              <div className="flex items-center justify-between">
                <div className="text-sm text-zinc-300">
                  Gültig bis <span className="font-mono text-zinc-100">{data.insurance.date_until}</span>
                </div>
                <StatusBadge status={data.insurance.status.includes("abgelaufen") ? "abgelaufen" : "aktiv"}/>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function CostCard({ label, value, hint, tone = "default", highlight }) {
  const colors = { default: "text-zinc-100", success: "text-emerald-400", warning: "text-amber-400", critical: "text-red-400" };
  const bg = highlight ? "bg-red-500/5 border-red-500/30" : "bg-zinc-900 border-zinc-800";
  return (
    <div className={`border rounded-md p-4 ${bg}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className={`text-2xl font-mono font-bold tabular-nums mt-2 ${colors[tone]}`}>{value}</div>
      {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

function VehicleTimeline({ vid }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/vehicles/${vid}/timeline`).then(({ data }) => { setEvents(data.events || []); setLoading(false); });
  }, [vid]);
  if (loading) return <div className="text-zinc-500 text-sm">Lade Verlauf...</div>;
  if (events.length === 0) return <Empty label="Keine Ereignisse"/>;

  const kindStyle = {
    handover: { dot: "bg-emerald-500", label: "Übergabe" },
    damage: { dot: "bg-red-500", label: "Schaden" },
    time_entry: { dot: "bg-blue-500", label: "Fahrt" },
    document: { dot: "bg-amber-500", label: "Dokument" },
  };

  const groups = {};
  events.forEach((e) => {
    const k = (e.ts || "").slice(0, 7);
    if (!groups[k]) groups[k] = [];
    groups[k].push(e);
  });

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6" data-testid="veh-timeline-tab">
      {Object.entries(groups).map(([month, items]) => (
        <div key={month} className="mb-6 last:mb-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-3 pb-1 border-b border-zinc-800">
            {new Date(month + "-01").toLocaleDateString("de-AT", { year: "numeric", month: "long" })}
          </div>
          <div className="space-y-3 relative pl-4 border-l border-zinc-800">
            {items.map((e, i) => {
              const s = kindStyle[e.kind] || { dot: "bg-zinc-500", label: e.kind };
              return (
                <div key={i} className="relative">
                  <div className={`absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full ${s.dot} ring-4 ring-zinc-900`}></div>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-xs text-zinc-500 font-mono tabular-nums shrink-0">{e.ts}</span>
                    <span className="text-[10px] uppercase tracking-wider text-zinc-600 font-semibold">{s.label}</span>
                    {e.status && <StatusBadge status={e.status}/>}
                  </div>
                  <div className="text-sm text-zinc-100 mt-0.5">{e.title}</div>
                  {e.meta && <div className="text-xs text-zinc-500 mt-0.5 font-mono">{e.meta}</div>}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function InfoPanel({ items }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {items.map(([k, v]) => (
        <div key={k} className="bg-zinc-900 border border-zinc-800 rounded p-4">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
          <div className="text-sm text-zinc-100 mt-1 font-mono">{v || "—"}</div>
        </div>
      ))}
    </div>
  );
}

function DocsTable({ docs }) {
  if (docs.length === 0) return <Empty label="Keine Dokumente" />;
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
          <th className="px-4 py-3 text-left">Typ</th><th className="px-4 py-3 text-left">Bezeichnung</th>
          <th className="px-4 py-3 text-left">Ablauf</th><th className="px-4 py-3 text-left">Status</th>
        </tr></thead>
        <tbody>{docs.map(d => (
          <tr key={d.id} className="border-b border-zinc-800"><td className="px-4 py-2.5 text-zinc-300">{d.doc_type}</td>
          <td className="px-4 py-2.5 text-zinc-300">{d.subject}</td><td className="px-4 py-2.5 font-mono text-xs text-zinc-400">{fmtDate(d.expiry_date)}</td>
          <td className="px-4 py-2.5"><StatusBadge status={d.doc_status}/></td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function AuditTable({ audit }) {
  if (audit.length === 0) return <Empty label="Keine Verlaufseinträge" />;
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
          <th className="px-4 py-3 text-left">Zeit</th><th className="px-4 py-3 text-left">Aktion</th><th className="px-4 py-3 text-left">Modul</th><th className="px-4 py-3 text-left">Benutzer</th>
        </tr></thead>
        <tbody>{audit.map(a => (
          <tr key={a.id} className="border-b border-zinc-800">
            <td className="px-4 py-2.5 font-mono text-xs text-zinc-400">{new Date(a.timestamp).toLocaleString("de-AT")}</td>
            <td className="px-4 py-2.5 text-zinc-300">{a.action}</td>
            <td className="px-4 py-2.5 text-zinc-400 font-mono text-xs">{a.module}</td>
            <td className="px-4 py-2.5 text-zinc-300">{a.user_name}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function Empty({ label }) { return <EmptyState label={label} />; }

/* ---------- Schäden ---------- */
function DamagesTab({ vehicleId, damages, employees, onChange, perms = { create: true, edit: true, delete: true } }) {
  const EMPTY = { vehicle_id: vehicleId, date: new Date().toISOString().slice(0,10), description: "", location: "", severity: "mittel", status: "offen", cost: 0, reported_by: "", driver_id: "", notes: "" };
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [photosFor, setPhotosFor] = useState(null);
  const [autoLinked, setAutoLinked] = useState(false);

  async function openCreate() {
    setEditing(null); setAutoLinked(false);
    let initial = { ...EMPTY };
    try {
      const { data: defaults } = await api.get(`/vehicles/${vehicleId}/defaults`);
      if (defaults.current_driver) {
        initial.driver_id = defaults.current_driver.id;
        setAutoLinked(true);
      }
    } catch {}
    setForm(initial);
    setShowForm(true);
  }

  async function save() {
    if (!form.description?.trim()) { toast.error("Beschreibung ist Pflichtfeld"); return; }
    try {
      const payload = { ...form, cost: +form.cost || 0 };
      if (editing) await api.put(`/damages/${editing.id}`, payload);
      else await api.post("/damages", payload);
      toast.success("Gespeichert"); setShowForm(false); setEditing(null); setForm(EMPTY); setAutoLinked(false); onChange();
    } catch { toast.error("Fehler beim Speichern"); }
  }
  async function remove(did) {
    if (!window.confirm("Schaden wirklich löschen?")) return;
    try { await api.delete(`/damages/${did}`); toast.success("Gelöscht"); onChange(); }
    catch { toast.error("Löschen fehlgeschlagen"); }
  }
  const empName = (eid) => { const e = employees.find(x => x.id === eid); return e ? `${e.first_name} ${e.last_name}` : "—"; };

  return (
    <div>
      <div className="flex justify-end mb-3">
        {perms.create && (
          <button data-testid="add-damage-btn" onClick={openCreate} className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded">
            <Plus className="w-3.5 h-3.5"/> Schaden
          </button>
        )}
      </div>
      {damages.length === 0 ? <Empty label="Keine Schäden erfasst" /> : (
        <div className="bg-zinc-900 border border-zinc-800 rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-3 text-left">Datum</th><th className="px-4 py-3 text-left">Beschreibung</th>
              <th className="px-4 py-3 text-left">Stelle</th><th className="px-4 py-3 text-left">Fahrer</th>
              <th className="px-4 py-3">Schwere</th><th className="px-4 py-3 text-right">Kosten</th>
              <th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Aktionen</th>
            </tr></thead>
            <tbody>{damages.map(d => (
              <tr key={d.id} className="border-b border-zinc-800 hover:bg-zinc-800/40">
                <td className="px-4 py-2.5 font-mono text-xs text-zinc-400">{fmtDate(d.date)}</td>
                <td className="px-4 py-2.5 text-zinc-100">{d.description}</td>
                <td className="px-4 py-2.5 text-zinc-300">{d.location||"—"}</td>
                <td className="px-4 py-2.5 text-zinc-300">{empName(d.driver_id)}</td>
                <td className="px-4 py-2.5 text-center"><StatusBadge status={d.severity === "schwer" ? "kritisch" : d.severity === "mittel" ? "warnung" : "ok"}/></td>
                <td className="px-4 py-2.5 text-right font-mono tabular-nums text-zinc-300">{fmtCur(d.cost)}</td>
                <td className="px-4 py-2.5"><StatusBadge status={d.status}/></td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={()=>setPhotosFor(d)} data-testid={`photos-damage-${d.id}`} className="p-1.5 text-zinc-400 hover:text-blue-400" title="Fotos">
                    <Camera className="w-3.5 h-3.5"/>
                    {(d.photos?.length || 0) > 0 && <span className="ml-1 text-[10px] font-mono text-blue-400">{d.photos.length}</span>}
                  </button>
                  {perms.edit && <button onClick={()=>{ setEditing(d); setForm({...EMPTY,...d}); setShowForm(true); }} className="p-1.5 text-zinc-400 hover:text-blue-400 ml-1"><Pencil className="w-3.5 h-3.5"/></button>}
                  {perms.delete && <button onClick={()=>remove(d.id)} className="p-1.5 text-zinc-400 hover:text-red-400 ml-1"><Trash2 className="w-3.5 h-3.5"/></button>}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {showForm && (
        <Modal title={editing ? "Schaden bearbeiten" : "Neuer Schaden"} onClose={()=>setShowForm(false)} onSave={save} saveTestId="damage-save-btn">
          {autoLinked && form.driver_id && (
            <div className="col-span-2 -mb-2 p-2.5 bg-emerald-500/5 border border-emerald-500/30 rounded text-xs text-emerald-400 flex items-center gap-1.5" data-testid="damage-auto-link-hint">
              ✓ Aktueller Fahrer automatisch verknüpft: <span className="font-medium">{empName(form.driver_id)}</span>
              <button type="button" onClick={()=>{ setAutoLinked(false); setForm(f => ({...f, driver_id: ""})); }} className="ml-auto text-zinc-500 hover:text-zinc-300 underline">Entfernen</button>
            </div>
          )}
          <F label="Datum" type="date" value={form.date} onChange={v=>setForm({...form, date:v})}/>
          <SelectF label="Schwere" value={form.severity} onChange={v=>setForm({...form,severity:v})} options={[["gering","gering"],["mittel","mittel"],["schwer","schwer"]]}/>
          <F label="Stelle am Fahrzeug" value={form.location} onChange={v=>setForm({...form,location:v})}/>
          <SelectF label="Fahrer" value={form.driver_id} onChange={v=>{ setForm({...form,driver_id:v}); setAutoLinked(false); }} options={[["",""],...employees.filter(e=>e.role==="fahrer" && (e.status !== "inaktiv" || e.id === form.driver_id)).map(e=>[e.id,`${e.first_name} ${e.last_name}${e.status === "inaktiv" ? " (abgemeldet)" : ""}`])]}/>
          <F label="Beschreibung *" value={form.description} onChange={v=>setForm({...form,description:v})} colSpan={2}/>
          <F label="Kosten €" type="number" value={form.cost} onChange={v=>setForm({...form,cost:v})}/>
          <SelectF label="Status" value={form.status} onChange={v=>setForm({...form,status:v})} options={[["offen","offen"],["in_bearbeitung","in Bearbeitung"],["behoben","behoben"]]}/>
          <F label="Gemeldet von" value={form.reported_by} onChange={v=>setForm({...form,reported_by:v})}/>
          <F label="Notizen" value={form.notes} onChange={v=>setForm({...form,notes:v})} textarea colSpan={2}/>
        </Modal>
      )}
      {photosFor && <DamagePhotos damage={photosFor} onClose={() => setPhotosFor(null)} onChange={onChange}/>}
    </div>
  );
}

function DamagePhotos({ damage, onClose, onChange }) {
  const [photos, setPhotos] = useState(damage.photos || []);
  const [uploading, setUploading] = useState(false);
  const api_url = process.env.REACT_APP_BACKEND_URL;

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      // Phase 5Z.42 — compress before upload
      const original = file;
      const compressed = await compressImage(file, { maxDimension: 1920, jpegQuality: 0.85 });
      if (compressed !== original && compressed.size < original.size) {
        toast.info(`Foto komprimiert: ${fmtBytes(original.size)} → ${fmtBytes(compressed.size)}`);
      }
      const fd = new FormData();
      fd.append("file", compressed);
      const { data } = await api.post(`/damages/${damage.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos([...photos, data]);
      toast.success("Foto hochgeladen");
      onChange();
    } catch (err) { console.warn("damage photo upload failed:", err); toast.error("Upload fehlgeschlagen"); }
    setUploading(false);
    const input = e.target;
    if (input) input.value = "";
  }

  async function removePhoto(pid) {
    if (!window.confirm("Foto löschen?")) return;
    await api.delete(`/damages/${damage.id}/photos/${pid}`);
    setPhotos(photos.filter((p) => p.id !== pid));
    onChange();
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Schaden</div>
            <h3 className="font-display font-semibold text-zinc-50 mt-0.5">{damage.description}</h3>
          </div>
          <button onClick={onClose} className="text-zinc-400"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5">
          <label className="flex flex-col items-center gap-2 cursor-pointer border-2 border-dashed border-zinc-700 hover:border-blue-500 rounded-md p-6 transition-colors" data-testid="damage-photo-drop">
            <Upload className="w-8 h-8 text-zinc-500"/>
            <div className="text-sm text-zinc-300">{uploading ? "Lädt hoch…" : "Foto auswählen oder hierher ziehen"}</div>
            <div className="text-xs text-zinc-500">JPG, PNG, HEIC</div>
            <input type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="damage-photo-input"/>
          </label>

          {photos.length > 0 && (
            <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-3">
              {photos.map((p) => (
                <div key={p.id} className="relative group border border-zinc-800 rounded overflow-hidden bg-zinc-950" data-testid={`damage-photo-${p.id}`}>
                  <img src={`${api_url}/api/damages/${damage.id}/photos/${p.id}`} alt={p.name} className="w-full h-40 object-cover"/>
                  <button onClick={() => removePhoto(p.id)} className="absolute top-1 right-1 p-1 bg-red-500/80 hover:bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-photo-${p.id}`}>
                    <Trash2 className="w-3 h-3"/>
                  </button>
                  <div className="p-2 text-xs text-zinc-400 font-mono truncate">{p.name}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Übergaben ---------- */
function HandoversTab({ vehicleId, handovers, employees, checklists, onChange, perms = { create: true, edit: true, delete: true } }) {
  const EMPTY = { vehicle_id: vehicleId, employee_id: "", direction: "uebergabe", date: new Date().toISOString().slice(0,10), km: 0, adblue_percent: 100, fuel_percent: 100, checklist_id: "", checklist_answers: [], damages_noted: "", signature: "", notes: "" };
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [expandedH, setExpandedH] = useState({});
  const [cardsPreview, setCardsPreview] = useState([]);

  useEffect(() => {
    if (showForm && form.employee_id) {
      api.get("/handovers/cards-preview", { params: { employee_id: form.employee_id } })
        .then(({ data }) => setCardsPreview(data));
    } else if (!form.employee_id) {
      setCardsPreview([]);
    }
  }, [showForm, form.employee_id]);

  const relevant = checklists.filter(c => ["uebergabe","ruecknahme"].includes(c.category));
  const empName = (eid) => { const e = employees.find(x => x.id === eid); return e ? `${e.first_name} ${e.last_name}` : "—"; };

  function pickChecklist(cid) {
    const cl = checklists.find(c => c.id === cid);
    setForm({ ...form, checklist_id: cid, checklist_answers: cl ? cl.items.map(it => ({ item: it.label, ok: false, note: "" })) : [] });
  }
  function setAnswer(idx, key, val) {
    const arr = [...form.checklist_answers]; arr[idx] = { ...arr[idx], [key]: val };
    setForm({ ...form, checklist_answers: arr });
  }
  async function save() {
    try {
      const payload = { ...form, km: +form.km||0, adblue_percent: +form.adblue_percent||0, fuel_percent: +form.fuel_percent||0 };
      await api.post("/handovers", payload);
      toast.success("Gespeichert"); setShowForm(false); setForm(EMPTY); onChange();
    } catch { toast.error("Fehler"); }
  }
  async function remove(hid) { if (!window.confirm("Löschen?")) return; await api.delete(`/handovers/${hid}`); toast.success("Gelöscht"); onChange(); }

  return (
    <div>
      <div className="flex justify-end mb-3">
        {perms.create && (
          <button data-testid="add-handover-btn" onClick={()=>setShowForm(true)} className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded">
            <Plus className="w-3.5 h-3.5"/> Übergabe / Rücknahme
          </button>
        )}
      </div>
      {handovers.length === 0 ? <Empty label="Keine Übergaben" /> : (
        <div className="bg-zinc-900 border border-zinc-800 rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-3 text-left w-8"></th>
              <th className="px-4 py-3 text-left">Datum</th><th className="px-4 py-3 text-left">Richtung</th>
              <th className="px-4 py-3 text-left">Fahrer</th><th className="px-4 py-3 text-right">KM</th>
              <th className="px-4 py-3 text-right">Tank</th><th className="px-4 py-3 text-right">AdBlue</th>
              <th className="px-4 py-3 text-left">Karten</th><th className="px-4 py-3 text-right">Aktion</th>
            </tr></thead>
            <tbody>{handovers.map(h => {
              const cards = h.cards_snapshot || [];
              return (
                <Fragment key={h.id}>
                  <tr className="border-b border-zinc-800 hover:bg-zinc-800/40">
                    <td className="px-4 py-2.5">
                      {cards.length > 0 ? (
                        <button onClick={() => setExpandedH(e => ({ ...e, [h.id]: !e[h.id] }))}
                          data-testid={`veh-handover-cards-toggle-${h.id}`}
                          className="text-zinc-400 hover:text-blue-400">
                          {expandedH[h.id] ? <ChevronDown className="w-3.5 h-3.5"/> : <ChevronRight className="w-3.5 h-3.5"/>}
                        </button>
                      ) : <span className="text-zinc-700 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-zinc-400">{fmtDate(h.date)}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={h.direction === "uebergabe" ? "aktiv" : "warnung"}/><span className="ml-2 text-zinc-300 text-xs">{h.direction === "uebergabe" ? "Übergabe" : "Rücknahme"}</span></td>
                    <td className="px-4 py-2.5 text-zinc-100">{empName(h.employee_id)}</td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums text-zinc-300">{(h.km||0).toLocaleString("de-AT")}</td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums text-zinc-300">{h.fuel_percent}%</td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums text-zinc-300">{h.adblue_percent}%</td>
                    <td className="px-4 py-2.5">{cards.length > 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs text-zinc-300"><CreditCard className="w-3 h-3"/> {cards.length}</span>
                    ) : <span className="text-zinc-700 text-xs">—</span>}</td>
                    <td className="px-4 py-2.5 text-right">{perms.delete ? <button onClick={()=>remove(h.id)} className="p-1.5 text-zinc-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5"/></button> : <span className="text-zinc-600 text-xs">—</span>}</td>
                  </tr>
                  {expandedH[h.id] && cards.length > 0 && (
                    <tr className="bg-zinc-950/60">
                      <td colSpan={9} className="px-6 py-3 border-b border-zinc-800">
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                          Übergebene Karten ({cards.length}) — Snapshot zum Übergabezeitpunkt
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {cards.map(c => (
                            <div key={c.id} className="bg-zinc-900 border border-zinc-800 rounded p-2.5 text-xs flex items-center gap-2">
                              {c.kind === "tank" ? <CreditCard className="w-3.5 h-3.5 text-blue-400"/> : <KeyRound className="w-3.5 h-3.5 text-amber-400"/>}
                              <div className="flex-1 min-w-0">
                                <div className="text-zinc-100 font-medium truncate">{c.provider || c.label || "—"}{c.label && c.provider ? <span className="text-zinc-500 font-normal"> · {c.label}</span> : null}</div>
                                <div className="text-zinc-500 font-mono text-[10px]">{c.card_number || "ohne Nr."}{c.expiry_date ? ` · gültig bis ${c.expiry_date}` : ""}</div>
                              </div>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${c.kind === "tank" ? "bg-blue-500/20 text-blue-300" : "bg-amber-500/20 text-amber-300"}`}>{c.kind === "tank" ? "Tankkarte" : "BG-Karte"}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}</tbody>
          </table>
        </div>
      )}
      {showForm && (
        <Modal title="Übergabe / Rücknahme" onClose={()=>setShowForm(false)} onSave={save} saveTestId="handover-save-btn" wide>
          <SelectF label="Richtung *" value={form.direction} onChange={v=>setForm({...form,direction:v})} options={[["uebergabe","Übergabe an Fahrer"],["ruecknahme","Rücknahme vom Fahrer"]]}/>
          <SelectF label="Fahrer *" value={form.employee_id} onChange={v=>setForm({...form,employee_id:v})} options={[["",""],...employees.filter(e=>e.role==="fahrer" && (e.status !== "inaktiv" || e.id === form.employee_id)).map(e=>[e.id,`${e.first_name} ${e.last_name}${e.status === "inaktiv" ? " (abgemeldet)" : ""}`])]}/>
          <F label="Datum" type="date" value={form.date} onChange={v=>setForm({...form,date:v})}/>
          <F label="KM-Stand" type="number" value={form.km} onChange={v=>setForm({...form,km:v})}/>
          <F label="Tank %" type="number" value={form.fuel_percent} onChange={v=>setForm({...form,fuel_percent:v})}/>
          <F label="AdBlue %" type="number" value={form.adblue_percent} onChange={v=>setForm({...form,adblue_percent:v})}/>
          <SelectF label="Checkliste" value={form.checklist_id} onChange={pickChecklist} options={[["",""],...relevant.map(c=>[c.id,c.name])]} colSpan={2}/>
          {form.checklist_answers?.length > 0 && (
            <div className="col-span-2 bg-zinc-950 border border-zinc-800 rounded p-3">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Checkliste durchgehen</div>
              <div className="space-y-1.5">
                {form.checklist_answers.map((a, i) => (
                  <label key={`chk-${a.item || i}`} className="flex items-center gap-2 text-sm text-zinc-200 cursor-pointer">
                    <input type="checkbox" checked={a.ok} onChange={(e)=>setAnswer(i, "ok", e.target.checked)} className="w-4 h-4 rounded border-zinc-700 bg-zinc-900 text-blue-600 focus:ring-blue-600"/>
                    <span className={a.ok ? "line-through text-zinc-500" : ""}>{a.item}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <F label="Vermerkte Schäden" value={form.damages_noted} onChange={v=>setForm({...form,damages_noted:v})} textarea colSpan={2}/>
          <F label="Unterschrift (Name)" value={form.signature} onChange={v=>setForm({...form,signature:v})}/>
          <F label="Notizen" value={form.notes} onChange={v=>setForm({...form,notes:v})} colSpan={2}/>
          {cardsPreview.length > 0 && (
            <div className="col-span-2 bg-blue-950/30 border border-blue-900/40 rounded p-3" data-testid="veh-handover-cards-preview">
              <div className="text-[10px] uppercase tracking-wider text-blue-300 mb-2 flex items-center gap-1.5">
                <CreditCard className="w-3 h-3"/>
                Diese {cardsPreview.length} Karte{cardsPreview.length === 1 ? "" : "n"} werden automatisch protokolliert
              </div>
              <div className="space-y-1">
                {cardsPreview.map(c => (
                  <div key={c.id} className="flex items-center gap-2 text-xs">
                    {c.kind === "tank" ? <CreditCard className="w-3 h-3 text-blue-400"/> : <KeyRound className="w-3 h-3 text-amber-400"/>}
                    <span className="text-zinc-100">{c.provider || c.label || "—"}</span>
                    {c.label && c.provider && <span className="text-zinc-500">· {c.label}</span>}
                    <span className="text-zinc-500 font-mono text-[10px] ml-auto">{c.card_number || "—"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

/* ---------- helpers ---------- */
function Modal({ title, children, onClose, onSave, saveTestId, wide }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className={`bg-zinc-900 border border-zinc-800 rounded-md w-full ${wide ? "max-w-3xl" : "max-w-2xl"} max-h-[90vh] overflow-y-auto`}>
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="font-display font-semibold text-zinc-50">{title}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">{children}</div>
        <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded">Abbrechen</button>
          <button onClick={onSave} data-testid={saveTestId} className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">Speichern</button>
        </div>
      </div>
    </div>
  );
}
function F({ label, value, onChange, type, textarea, colSpan = 1 }) {
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</label>
      {textarea
        ? <textarea rows={2} value={value||""} onChange={(e)=>onChange(e.target.value)} className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
        : <input type={type||"text"} value={value||""} onChange={(e)=>onChange(e.target.value)} className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
      }
    </div>
  );
}
function SelectF({ label, value, onChange, options, colSpan = 1 }) {
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</label>
      <select value={value||""} onChange={(e)=>onChange(e.target.value)} className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

/* ---------- Tankkarten ---------- */
function FuelCardsTab({ vehicleId, items, onChange, perms = { create: true, edit: true, delete: true } }) {
  const EMPTY = { vehicle_id: vehicleId, card_number: "", provider: "OMV", holder: "Munja Trans GmbH", pin: "", expiry_date: "", monthly_limit: 1500, status: "aktiv", notes: "" };
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  async function save() {
    try {
      const payload = { ...form, monthly_limit: +form.monthly_limit||0 };
      if (editing) await api.put(`/fuel-cards/${editing.id}`, payload);
      else await api.post("/fuel-cards", payload);
      toast.success("Gespeichert"); setShowForm(false); setEditing(null); setForm(EMPTY); onChange();
    } catch { toast.error("Fehler"); }
  }
  async function remove(fid) { if (!window.confirm("Tankkarte löschen?")) return; await api.delete(`/fuel-cards/${fid}`); toast.success("Gelöscht"); onChange(); }

  return (
    <div>
      <div className="flex justify-end mb-3">
        {perms.create && (
          <button data-testid="add-fuelcard-btn" onClick={()=>{ setEditing(null); setForm(EMPTY); setShowForm(true); }} className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded">
            <Plus className="w-3.5 h-3.5"/> Tankkarte
          </button>
        )}
      </div>
      {items.length === 0 ? <Empty label="Keine Tankkarten" /> : (
        <div className="bg-zinc-900 border border-zinc-800 rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-3 text-left">Kartennummer</th><th className="px-4 py-3 text-left">Anbieter</th>
              <th className="px-4 py-3 text-left">Inhaber</th><th className="px-4 py-3 text-left">Ablauf</th>
              <th className="px-4 py-3 text-right">Monatslimit</th><th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Aktionen</th>
            </tr></thead>
            <tbody>{items.map(f => (
              <tr key={f.id} className="border-b border-zinc-800 hover:bg-zinc-800/40">
                <td className="px-4 py-2.5 font-mono text-xs text-zinc-100">{f.card_number}</td>
                <td className="px-4 py-2.5 text-zinc-300">{f.provider}</td>
                <td className="px-4 py-2.5 text-zinc-400 text-xs">{f.holder}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-zinc-400">{fmtDate(f.expiry_date)}</td>
                <td className="px-4 py-2.5 text-right font-mono tabular-nums text-zinc-300">€ {(f.monthly_limit||0).toFixed(0)}</td>
                <td className="px-4 py-2.5"><StatusBadge status={f.status}/></td>
                <td className="px-4 py-2.5 text-right">
                  {perms.edit && <button onClick={()=>{ setEditing(f); setForm({...EMPTY, ...f}); setShowForm(true); }} className="p-1.5 text-zinc-400 hover:text-blue-400"><Pencil className="w-3.5 h-3.5"/></button>}
                  {perms.delete && <button onClick={()=>remove(f.id)} className="p-1.5 text-zinc-400 hover:text-red-400 ml-1"><Trash2 className="w-3.5 h-3.5"/></button>}
                  {!perms.edit && !perms.delete && <span className="text-zinc-600 text-xs">—</span>}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {showForm && (
        <Modal title={editing?"Tankkarte bearbeiten":"Neue Tankkarte"} onClose={()=>setShowForm(false)} onSave={save} saveTestId="fuelcard-save-btn">
          <F label="Kartennummer *" value={form.card_number} onChange={v=>setForm({...form,card_number:v})}/>
          <SelectF label="Anbieter" value={form.provider} onChange={v=>setForm({...form,provider:v})} options={[["OMV","OMV"],["BP","BP"],["Shell","Shell"],["Eurowag","Eurowag"],["DKV","DKV"],["UTA","UTA"]]}/>
          <F label="Inhaber" value={form.holder} onChange={v=>setForm({...form,holder:v})}/>
          <F label="PIN" value={form.pin} onChange={v=>setForm({...form,pin:v})}/>
          <F label="Ablaufdatum" type="date" value={form.expiry_date} onChange={v=>setForm({...form,expiry_date:v})}/>
          <F label="Monatslimit €" type="number" value={form.monthly_limit} onChange={v=>setForm({...form,monthly_limit:v})}/>
          <SelectF label="Status" value={form.status} onChange={v=>setForm({...form,status:v})} options={[["aktiv","aktiv"],["gesperrt","gesperrt"],["verloren","verloren"]]}/>
          <F label="Notizen" value={form.notes} onChange={v=>setForm({...form,notes:v})} colSpan={2}/>
        </Modal>
      )}
    </div>
  );
}

/* ---------- Leasing ---------- */
function LeasingsTab({ vehicleId, items, onChange, perms = { create: true, edit: true, delete: true } }) {
  const EMPTY = { vehicle_id: vehicleId, provider: "", contract_number: "", monthly_rate: 0, start_date: "", end_date: "", total_km_limit: 0, residual_value: 0, status: "aktiv", notes: "" };
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  async function save() {
    try {
      const payload = { ...form, monthly_rate: +form.monthly_rate||0, total_km_limit: +form.total_km_limit||0, residual_value: +form.residual_value||0 };
      if (editing) await api.put(`/leasings/${editing.id}`, payload);
      else await api.post("/leasings", payload);
      toast.success("Gespeichert"); setShowForm(false); setEditing(null); setForm(EMPTY); onChange();
    } catch { toast.error("Fehler"); }
  }
  async function remove(lid) { if (!window.confirm("Leasing löschen?")) return; await api.delete(`/leasings/${lid}`); toast.success("Gelöscht"); onChange(); }

  return (
    <div>
      <div className="flex justify-end mb-3">
        {perms.create && (
          <button data-testid="add-leasing-btn" onClick={()=>{ setEditing(null); setForm(EMPTY); setShowForm(true); }} className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded">
            <Plus className="w-3.5 h-3.5"/> Leasing
          </button>
        )}
      </div>
      {items.length === 0 ? <Empty label="Keine Leasing-Verträge" /> : (
        <div className="bg-zinc-900 border border-zinc-800 rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-3 text-left">Anbieter</th><th className="px-4 py-3 text-left">Vertrag</th>
              <th className="px-4 py-3 text-left">Laufzeit</th><th className="px-4 py-3 text-right">Rate/Monat</th>
              <th className="px-4 py-3 text-right">KM-Limit</th><th className="px-4 py-3 text-right">Restwert</th>
              <th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Aktionen</th>
            </tr></thead>
            <tbody>{items.map(l => (
              <tr key={l.id} className="border-b border-zinc-800 hover:bg-zinc-800/40">
                <td className="px-4 py-2.5 text-zinc-100 font-medium">{l.provider}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-zinc-400">{l.contract_number||"—"}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-zinc-400">{fmtDate(l.start_date)} → {fmtDate(l.end_date)}</td>
                <td className="px-4 py-2.5 text-right font-mono tabular-nums text-zinc-300">{fmtCur(l.monthly_rate)}</td>
                <td className="px-4 py-2.5 text-right font-mono tabular-nums text-zinc-300">{(l.total_km_limit||0).toLocaleString("de-AT")}</td>
                <td className="px-4 py-2.5 text-right font-mono tabular-nums text-zinc-300">€ {(l.residual_value||0).toFixed(0)}</td>
                <td className="px-4 py-2.5"><StatusBadge status={l.status}/></td>
                <td className="px-4 py-2.5 text-right">
                  {perms.edit && <button onClick={()=>{ setEditing(l); setForm({...EMPTY, ...l}); setShowForm(true); }} className="p-1.5 text-zinc-400 hover:text-blue-400"><Pencil className="w-3.5 h-3.5"/></button>}
                  {perms.delete && <button onClick={()=>remove(l.id)} className="p-1.5 text-zinc-400 hover:text-red-400 ml-1"><Trash2 className="w-3.5 h-3.5"/></button>}
                  {!perms.edit && !perms.delete && <span className="text-zinc-600 text-xs">—</span>}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {showForm && (
        <Modal title={editing?"Leasing bearbeiten":"Neuer Leasing-Vertrag"} onClose={()=>setShowForm(false)} onSave={save} saveTestId="leasing-save-btn">
          <F label="Anbieter *" value={form.provider} onChange={v=>setForm({...form,provider:v})}/>
          <F label="Vertragsnummer" value={form.contract_number} onChange={v=>setForm({...form,contract_number:v})}/>
          <F label="Startdatum" type="date" value={form.start_date} onChange={v=>setForm({...form,start_date:v})}/>
          <F label="Enddatum" type="date" value={form.end_date} onChange={v=>setForm({...form,end_date:v})}/>
          <F label="Monatsrate €" type="number" value={form.monthly_rate} onChange={v=>setForm({...form,monthly_rate:v})}/>
          <F label="KM-Limit" type="number" value={form.total_km_limit} onChange={v=>setForm({...form,total_km_limit:v})}/>
          <F label="Restwert €" type="number" value={form.residual_value} onChange={v=>setForm({...form,residual_value:v})}/>
          <SelectF label="Status" value={form.status} onChange={v=>setForm({...form,status:v})} options={[["aktiv","aktiv"],["auslaufend","auslaufend"],["beendet","beendet"]]}/>
          <F label="Notizen" value={form.notes} onChange={v=>setForm({...form,notes:v})} textarea colSpan={2}/>
        </Modal>
      )}
    </div>
  );
}

